import { MIN_SPAWN_INTERVAL_MS } from './asteroid-field';
import { isCharacterId, type CharacterId } from './characters';
import { normaliseInitials } from './initials';
import { SCORE_PER_NEAR_MISS, SCORE_PER_SECOND } from './race-state';

/**
 * The global high score board, as pure rules.
 *
 * Everything here runs in two places: in the browser, so the game can avoid
 * submitting a run it already knows is invalid, and in the Lambda, where the
 * same functions are the actual gate. One definition, two callers — a rule that
 * only existed on the client would be no rule at all, and one that only existed
 * on the server would let the client submit rubbish and find out later.
 *
 * Spec: docs/global-leaderboard.md
 */

/** The only board that exists today. `character#<id>` boards are reserved. */
export const GLOBAL_BOARD = 'global';

/** How many places the board shows. Storage keeps everything; this is a view. */
export const BOARD_SIZE = 10;

/** Longer than this is not a run, it is a stuck tab or a forged payload. */
export const MAX_SURVIVED_MS = 24 * 60 * 60 * 1_000;

/** Width of the score segment of the sort key. Ten digits outlast any board. */
export const SCORE_KEY_DIGITS = 10;

/** Two 32-bit lanes, hex encoded. */
export const ENTRY_ID_LENGTH = 16;

export interface LeaderboardEntry {
  initials: string;
  character: CharacterId;
  score: number;
  survivedMs: number;
  /** Epoch milliseconds. */
  recordedAt: number;
}

/** What a client sends. The server decides `recordedAt`, never the client. */
export interface ScoreSubmission {
  initials: string;
  character: CharacterId;
  score: number;
  survivedMs: number;
  /** The run's field seed, kept so the score can be re-checked later. */
  seed: number;
}

export type Validation<T> = { ok: true; value: T } | { ok: false; reason: string };

/**
 * The highest score a run of this length could honestly have produced.
 *
 * Score is `floor(seconds × 100) + nearMisses × 25`, and near misses cannot
 * outnumber the rocks that spawned. Rocks bottom out at one per
 * {@link MIN_SPAWN_INTERVAL_MS}.
 *
 * This is deliberately generous: it assumes the spawn floor from the first
 * second, when the real interval takes a full minute to ease down to it. A
 * genuine 60s run tops out near 9,700 and this allows nearly 14,000. The point
 * is to reject the absurd, not to police good play.
 */
export function maxPlausibleScore(survivedMs: number): number {
  if (!Number.isFinite(survivedMs) || survivedMs <= 0) {
    return 0;
  }
  const survival = Math.floor((survivedMs / 1_000) * SCORE_PER_SECOND);
  const mostRocks = Math.ceil(survivedMs / MIN_SPAWN_INTERVAL_MS);
  return survival + mostRocks * SCORE_PER_NEAR_MISS;
}

/**
 * Checks an untrusted payload and returns it typed, or says why not.
 *
 * A client-side game cannot prove its scores — anyone can POST a number. This
 * catches the impossible rather than the merely suspicious; see the anti-cheat
 * section of the spec for what that does and does not buy.
 */
export function validateSubmission(raw: unknown): Validation<ScoreSubmission> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { ok: false, reason: 'submission must be an object' };
  }
  const body = raw as Record<string, unknown>;

  if (typeof body['initials'] !== 'string') {
    return { ok: false, reason: 'initials must be a string' };
  }
  if (typeof body['character'] !== 'string' || !isCharacterId(body['character'])) {
    return { ok: false, reason: 'character is not on the roster' };
  }

  const score = body['score'];
  if (typeof score !== 'number' || !Number.isFinite(score) || !Number.isInteger(score)) {
    return { ok: false, reason: 'score must be a whole number' };
  }
  if (score <= 0) {
    return { ok: false, reason: 'score must be above zero' };
  }

  const survivedMs = body['survivedMs'];
  if (typeof survivedMs !== 'number' || !Number.isFinite(survivedMs) || survivedMs < 0) {
    return { ok: false, reason: 'survivedMs must be a positive number' };
  }
  if (survivedMs > MAX_SURVIVED_MS) {
    return { ok: false, reason: 'survivedMs is longer than any real run' };
  }

  const seed = body['seed'];
  if (typeof seed !== 'number' || !Number.isFinite(seed)) {
    return { ok: false, reason: 'seed must be a number' };
  }

  if (score > maxPlausibleScore(survivedMs)) {
    return { ok: false, reason: 'score is impossible for a run of that length' };
  }

  return {
    ok: true,
    value: {
      initials: normaliseInitials(body['initials']),
      character: body['character'],
      score,
      survivedMs,
      seed,
    },
  };
}

/** The fields that say *which run this is*, and nothing else. */
export type RunIdentity = Pick<
  LeaderboardEntry,
  'initials' | 'character' | 'score' | 'survivedMs'
>;

/**
 * A stable id for a run, derived from the run itself.
 *
 * This is what makes submitting idempotent. A finished race is submitted with
 * its real field seed, and the local-cache migration later pushes the same race
 * with `seed: 0` — so the seed is deliberately *not* part of the identity, nor
 * is the timestamp, or the same race would hash two ways and land twice. That
 * is not hypothetical: it is the bug this replaced.
 *
 * FNV-1a rather than a real digest, because this is an identity, not a security
 * boundary, and it has to run unchanged in both the browser and the Lambda
 * without either pulling in a hashing library or an async crypto API.
 */
export function entryId(run: RunIdentity): string {
  const canonical = [
    normaliseInitials(run.initials),
    run.character,
    Math.max(0, Math.floor(run.score)),
    Math.max(0, Math.round(run.survivedMs)),
  ].join('|');
  // Two lanes with different offset bases, so the id is 64 bits rather than 32.
  return `${fnv1a(canonical, 0x811c9dc5)}${fnv1a(canonical, 0x01000193)}`;
}

/**
 * The DynamoDB sort key, built so a descending query returns the board in
 * order without the reader sorting anything.
 *
 * Zero padding makes a string sort agree with a numeric one — without it '9'
 * outranks '10'. The id makes the key deterministic, so a `PutItem` guarded by
 * `attribute_not_exists` stores a given run exactly once no matter how many
 * times it is submitted.
 *
 * Ties are therefore stored in arbitrary order rather than oldest-first; the
 * reader restores the arcade convention with {@link byRank}.
 */
export function rankKey(score: number, id: string): string {
  const paddedScore = String(Math.max(0, Math.floor(score))).padStart(SCORE_KEY_DIGITS, '0');
  return `${paddedScore}#${id}`;
}

function fnv1a(text: string, basis: number): string {
  let hash = basis >>> 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash ^ text.charCodeAt(i)) >>> 0;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * Combines the global board with whatever the local cache holds.
 *
 * Used when the network is unreachable, and in the moment between submitting a
 * score and the board coming back, so the player always sees their own run.
 */
export function mergeEntries(
  remote: readonly LeaderboardEntry[],
  local: readonly LeaderboardEntry[],
): LeaderboardEntry[] {
  const byIdentity = new Map<string, LeaderboardEntry>();
  for (const item of [...remote, ...local]) {
    byIdentity.set(identity(item), item);
  }
  return [...byIdentity.values()].sort(byRank).slice(0, BOARD_SIZE);
}

/** Highest score first; the earlier run wins a tie. */
export function byRank(a: LeaderboardEntry, b: LeaderboardEntry): number {
  return b.score - a.score || a.recordedAt - b.recordedAt;
}

/** Two rows describe the same run when every field of it agrees. */
function identity(entry: LeaderboardEntry): string {
  return [
    entry.initials,
    entry.character,
    entry.score,
    entry.survivedMs,
    entry.recordedAt,
  ].join('|');
}
